import { useMemo, useRef } from 'react';

/**
 * A hook that returns a stable function reference that always calls
 * the latest version of the given `callback`. Useful for avoiding
 * unnecessary re-renders when passing callbacks to child components.
 *
 * @typeParam T - The type of function being used as a callback.
 * @param callback - The function whose updated behavior you want to preserve
 * @returns A stable function reference that always calls the latest version of `callback`
 */
export const useStableCallback = <T extends (...args: any[]) => any>(callback: T): T => {
    const cbRef = useRef(callback);
    cbRef.current = callback;
    return useMemo(() => ((...args: Parameters<T>) => cbRef.current(...args)) as T, []);
};
